import styled from 'styled-components';

import logo from '../../assets/logo.png';
import SectionHeader from '../ui/SectionHeader';
import Reveal from '../ui/Reveal';
import { translations } from '../../data/i18n';
import { galleryItems } from '../../data/gallery';

const Section = styled.section`
  padding: 98px 28px;
  background:
    radial-gradient(circle at 20% 0%, rgba(221,161,47,0.12), transparent 32%),
    linear-gradient(135deg, #070605, #11100e 58%, #1d1813);
  color: var(--white);
  overflow: hidden;
`;

const Grid = styled.div`
  max-width: var(--container);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 34px;
  align-items: stretch;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled.div`
  min-height: 430px;
  border-radius: 28px;
  padding: clamp(28px, 4vw, 46px);
  position: relative;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04));
  border: 1px solid rgba(255,255,255,0.11);
  box-shadow: 0 28px 80px rgba(0,0,0,0.32);

  &::after {
    content: '';
    position: absolute;
    right: -70px;
    bottom: -90px;
    width: 430px;
    height: 430px;
    background: url(${logo}) center/contain no-repeat;
    opacity: 0.12;
    filter: grayscale(0.25);
    pointer-events: none;
  }

  @media (max-width: 680px) {
    min-height: 360px;
  }
`;

const FeatureContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 680px;
`;

const FeatureEyebrow = styled.div`
  color: var(--gold-light);
  font-weight: 950;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin-bottom: 16px;
`;

const FeatureTitle = styled.h3`
  margin: 0 0 22px;
  color: var(--white);
  font-size: clamp(2.45rem, 5vw, 5.1rem);
  line-height: 0.95;
  letter-spacing: -3px;
  text-transform: uppercase;

  @media (max-width: 680px) {
    letter-spacing: -1.5px;
  }
`;

const FeatureText = styled.p`
  max-width: 590px;
  margin: 0;
  color: rgba(255,255,255,0.82);
  font-size: 1.14rem;
  line-height: 1.7;

  @media (max-width: 680px) {
    font-size: 1rem;
  }
`;

const Cards = styled.div`
  display: grid;
  gap: 18px;
`;

const Card = styled.article`
  padding: 26px;
  border-radius: 22px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.10);
  backdrop-filter: blur(12px);
  transition:
    transform 0.22s ease,
    background 0.22s ease,
    border-color 0.22s ease;

  &:hover {
    transform: translateY(-4px);
    background: rgba(255,255,255,0.11);
    border-color: rgba(244,197,93,0.28);
  }

  strong {
    display: block;
    color: var(--gold-light);
    font-size: 1.2rem;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  p {
    margin: 0;
    color: rgba(255,255,255,0.76);
    line-height: 1.6;
  }
`;

const Icon = styled.div`
  font-size: 1.9rem;
  margin-bottom: 14px;
`;

const CTA = styled.a`
  display: inline-flex;
  align-items: center;
  margin-top: 30px;
  padding: 15px 22px;
  border-radius: 13px;
  background: linear-gradient(135deg, var(--gold), var(--gold-light));
  color: #fff;
  font-weight: 950;
  text-transform: uppercase;
  box-shadow: 0 16px 34px rgba(221,161,47,0.28);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

function GallerySection({ language }) {
  const t = translations[language].gallery;

  const featureTitle =
    language === 'de'
      ? 'Echte Styles. Persönlich gemacht.'
      : 'Real styles. Personally made.';

  const featureText =
    language === 'de'
      ? 'Ob Braids, Cornrows, Twists oder Dreadlocks: Jeder Look wird persönlich besprochen und mit Gefühl für Haar, Stil und Alltag umgesetzt.'
      : 'Whether braids, cornrows, twists or dreadlocks: every look is personally discussed and created with care for hair, style and everyday life.';

  return (
    <Section id="gallery">
      <Reveal>
        <SectionHeader eyebrow={t.subtitle} title={t.title} />
      </Reveal>

      <Grid>
        <Reveal direction="right">
          <Feature>
            <FeatureContent>
              <FeatureEyebrow>Hakuna Matata</FeatureEyebrow>
              <FeatureTitle>{featureTitle}</FeatureTitle>
              <FeatureText>{featureText}</FeatureText>
              <CTA href="#booking">{t.button} →</CTA>
            </FeatureContent>
          </Feature>
        </Reveal>

        <Cards>
          {galleryItems.map((item, index) => {
            const text = item[language] || item.de;

            return (
              <Reveal key={item.id} delay={index * 120} direction="left">
                <Card>
                  <Icon>{item.icon}</Icon>
                  <strong>{text.title}</strong>
                  <p>{text.text}</p>
                </Card>
              </Reveal>
            );
          })}
        </Cards>
      </Grid>
    </Section>
  );
}

export default GallerySection;